import { _ as _applyDecoratedDescriptor, a as _initializerDefineProperty } from '../_rollupPluginBabelHelpers-3b0ba74f.js';
import Component from '@glimmer/component';
import Pikaday from 'pikaday';
import { action } from '@ember/object';
import { inject } from '@ember/service';
import { task, timeout } from 'ember-concurrency';
import { tracked } from '@glimmer/tracking';
import { guidFor } from '@ember/object/internals';
import moment from 'moment-timezone';
import { safeTimeZone } from 'beebee-base/lib/utility';
import { precompileTemplate } from '@ember/template-compilation';
import { setComponentTemplate } from '@ember/component';

var TEMPLATE = precompileTemplate("<div\n  {{did-insert this.setupPickers}}\n  {{did-update this.syncOriginalDate @originalDate}}\n  class=\'datetime-wrap {{if this.pickTime \"timepicker-enable\"}} {{if this.hideDate \"hide-date\"}}\'\n>\n  <div class=\'date-wrap {{if this.hideDate \"hide\"}}\'>\n    <Input\n      type=\'text\'\n      id=\'datepicker{{this.elementId}}\'\n      placeholder={{this.placeholder}}\n      disabled={{@disabled}}\n      value={{this.displayDate}}\n      autocomplete=\'off\'\n      {{on \'input\' this.dateInput}}\n      {{on \'focusout\' this.dateFocusOut}}\n      ...attributes\n    />\n  </div>\n\n  {{#if this.pickTime}}\n    <div class=\'time-wrap {{if @pickSeconds \"with-seconds\"}}\'>\n      <input\n        type=\'time\'\n        id=\'timepicker{{this.elementId}}\'\n        value={{this.timeValue}}\n        disabled={{@disabled}}\n        step={{if @pickSeconds \'1\' \'60\'}}\n        {{on \'input\' (perform this._updateTimeTask)}}\n        ...attributes\n      />\n    </div>\n  {{/if}}\n</div>");

var _dec, _dec2, _dec3, _dec4, _class, _descriptor, _descriptor2, _descriptor3, _descriptor4, _descriptor5, _descriptor6, _descriptor7, _descriptor8, _descriptor9, _descriptor0, _BbInputDateComponent;
let BbInputDateComponent = (_dec = task(function* (event) {
  const value = event?.target?.value;
  if (!value) return;

  // Aggiorna timeValue subito per evitare che un re-render durante il yield resetti l'input
  this.timeValue = value;
  yield timeout(500);
  if (this.isDestroying || this.isDestroyed) return;
  let hours,
    minutes,
    seconds = 0;
  if (this.args.pickSeconds) {
    [hours, minutes, seconds] = value.split(':').map(Number);
  } else {
    [hours, minutes] = value.split(':').map(Number);
  }
  const zone = safeTimeZone(this.args.timeZone || 'Europe/Rome');
  let originalDate = this.originalDate && moment(this.originalDate).isValid() ? moment(this.originalDate) : moment();
  originalDate = originalDate.tz(zone, true).hour(hours).minute(minutes).second(seconds || 0);
  const formatted = originalDate.format(this.internalFormat);
  const timeFormat = this.args.pickSeconds ? 'HH:mm:ss' : 'HH:mm';
  this.timeValue = originalDate.format(timeFormat);
  this.originalDate = formatted;
  this.args.setDate?.(formatted);
  this.args.action?.(formatted);
}).restartable(), _dec2 = task(function* (dateMoment) {
  yield timeout(50);
  if (this.isDestroying || this.isDestroyed) return;
  if (!dateMoment || !moment(dateMoment, this.internalFormat, true).isValid()) {
    this.originalDate = null;
    this._updatePickerTask.perform();
    return;
  }
  const zone = safeTimeZone(this.args.timeZone || 'Europe/Rome');
  let formatDate = moment(dateMoment);
  formatDate = formatDate.tz(zone, true);
  let originalDate = this.originalDate ? moment(this.originalDate) : moment();
  originalDate.year(formatDate.year()).month(formatDate.month()).date(formatDate.date());
  if (this.pickTime) {
    this._setTimeOnDate(originalDate);
  } else {
    // Quando si seleziona solo la data (senza ora), imposta mezzogiorno nel timezone
    // dell'utente per evitare che la conversione di timezone sposti il giorno
    originalDate = moment.tz({
      year: formatDate.year(),
      month: formatDate.month(),
      date: formatDate.date(),
      hour: 12,
      minute: 0,
      second: 0
    }, zone);
  }
  const formatted = originalDate.format(this.internalFormat);
  this.originalDate = formatted;
  this.args.setDate?.(formatted);
  this.args.action?.(formatted);
}), _dec3 = task(function* () {
  // Usa yield per throttling naturale
  yield timeout(50);
  if (this.isDestroying || this.isDestroyed) return;
  if (!this.pikaday) {
    return;
  }
  if (this._isUserActivelyInteracting()) {
    return;
  }
  const zone = safeTimeZone(this.args.timeZone || 'Europe/Rome');
  const date = this.originalDate && moment(this.originalDate).isValid() ? moment(this.originalDate).tz(zone) : null;
  if (date?.isValid()) {
    try {
      // 🔑 Usa stringa YYYY-MM-DD come da manuale Pikaday
      this.pikaday.setDate(date.toDate(), true);

      // Aggiorna manualmente l'input
      const formatted = date.format(this.momentFormat);
      this._updateInputValue(formatted);
    } catch (e) {
      this.log.error('input-date: date format/update failed', e);
    }
  }
}), _dec4 = task(function* (value) {
  if (!this.pikaday?.isVisible()) {
    return false;
  }

  // Per input potenzialmente incompleti o invalidi, usa il debounce
  yield timeout(1000);
  if (this.isDestroying || this.isDestroyed) return;

  // Se il campo è vuoto, cancella immediatamente
  if (!value || value === '') {
    this.args.setDate?.(null);
    this.args.action?.(null);
    this.originalDate = null;
    this._scheduleUpdate(() => this._updatePickerTask.perform(), 50);
    return;
  }

  // Controllo per date valide - se è valida, aggiorna subito
  const validation = this._parseAndValidateDate(value);
  if (validation.isValid) {
    if (this.pickTime) {
      this._setTimeOnDate(validation.date);
    } else {
      const zone = safeTimeZone(this.args.timeZone || 'Europe/Rome');
      validation.date = moment.tz({
        year: validation.date.year(),
        month: validation.date.month(),
        date: validation.date.date(),
        hour: 12,
        minute: 0,
        second: 0
      }, zone);
    }
    this.originalDate = validation.date.format(this.internalFormat);
    this._updateInputValue(validation.formatted);
    this.args.setDate?.(this.originalDate);
    this.args.action?.(this.originalDate);
    this._scheduleUpdate(() => this._updatePickerTask.perform(true), 50);

    // 🔑 Chiudi il datepicker se l'utente ha scritto una data valida
    if (this.pikaday?.isVisible()) {
      this.pikaday.hide();
    }
    return;
  }

  // Evita conflitti se l'utente sta ancora interagendo con Pikaday (dopo il debounce)
  if (this.isUserInteracting) {
    return;
  }
  this._processDateInput(value);
}).restartable(), _class = (_BbInputDateComponent = class BbInputDateComponent extends Component {
  get placeholder() {
    return this.args.placeholder || `e.g. ${BbInputDateComponent._defaultPlaceholder}`;
  }
  get elementId() {
    if (!this._elementId) {
      this._elementId = guidFor(this);
    }
    return this._elementId;
  }
  get momentFormat() {
    return 'DD/MM/YYYY';
  }
  get displayDate() {
    if (!this.originalDate) return '';
    const m = moment(this.originalDate);
    return m.isValid() ? m.format(this.momentFormat) : '';
  }
  get internalFormat() {
    if (this.pickTime) {
      return this.args.pickSeconds ? 'YYYY-MM-DDTHH:mm:ssZ' : 'YYYY-MM-DDTHH:mmZ';
    }
    return 'YYYY-MM-DDTHH:mm:ssZ';
  }
  get hideDate() {
    return this.args.hideDate ?? false;
  }
  get pickTime() {
    return this.args.pickTime ?? false;
  }
  get language() {
    return this.args.language ?? 'it';
  }
  get yearRange() {
    return this.args.yearRange ?? 100;
  }
  constructor() {
    super(...arguments);
    _initializerDefineProperty(this, "intl", _descriptor, this);
    _initializerDefineProperty(this, "log", _descriptor2, this);
    _initializerDefineProperty(this, "pikaday", _descriptor3, this);
    _initializerDefineProperty(this, "timeValue", _descriptor4, this);
    _initializerDefineProperty(this, "originalDate", _descriptor5, this);
    _initializerDefineProperty(this, "isUserInteracting", _descriptor6, this);
    this._timeouts = [];
    _initializerDefineProperty(this, "_updateTimeTask", _descriptor7, this);
    _initializerDefineProperty(this, "_updateDateTask", _descriptor8, this);
    _initializerDefineProperty(this, "_updatePickerTask", _descriptor9, this);
    // Bound handlers riutilizzabili (creati una sola volta per istanza)
    this._onSelectMousedown = () => {
      this.isUserInteracting = true;
    };
    this._onSelectChange = () => {
      const id = setTimeout(() => {
        if (this.isDestroying || this.isDestroyed) return;
        this.isUserInteracting = false;
        this._timeouts = this._timeouts.filter(t => t !== id);
      }, 100);
      this._timeouts.push(id);
    };
    this._onSelectBlur = () => {
      const id = setTimeout(() => {
        if (this.isDestroying || this.isDestroyed) return;
        this.isUserInteracting = false;
        this._timeouts = this._timeouts.filter(t => t !== id);
      }, 50);
      this._timeouts.push(id);
    };
    this._onButtonMousedown = () => {
      this.isUserInteracting = true;
    };
    this._onButtonClick = () => {
      const id = setTimeout(() => {
        if (this.isDestroying || this.isDestroyed) return;
        this.isUserInteracting = false;
        this._timeouts = this._timeouts.filter(t => t !== id);
      }, 50);
      this._timeouts.push(id);
    };
    _initializerDefineProperty(this, "_dateInputTask", _descriptor0, this);
    if (typeof this.args.setDate !== 'function') {
      throw new Error('BbInputDateComponent requires a setDate function in args');
    }
  }
  syncOriginalDate() {
    if (this.originalDate !== this.args.originalDate) {
      this.originalDate = this.args.originalDate;
      if (this.originalDate && moment(this.originalDate).isValid()) {
        const format = this.args.pickSeconds ? 'HH:mm:ss' : 'HH:mm';
        this.timeValue = moment(this.originalDate).format(format);
      } else {
        this.timeValue = null;
      }
      this._updatePickerTask.perform();
    }
  }
  willDestroy() {
    // [regression-fix] CAT-5 — duplicate super.willDestroy() call removed; parent hook was
    // being invoked twice (bare call + optional-chaining call), risking double-teardown.
    super.willDestroy(...arguments);

    // Cleanup di tutti i timeout
    this._timeouts.forEach(id => clearTimeout(id));
    this._timeouts = [];

    // Cleanup completo per evitare memory leak
    this.isUserInteracting = false;
    try {
      // Rimuovi tutti i listener personalizzati prima di distruggere
      if (this.pikaday?.el) {
        const pikadayEl = this.pikaday.el;
        const selects = pikadayEl.querySelectorAll('select');
        selects.forEach(el => {
          el.removeEventListener('mousedown', this._onSelectMousedown);
          el.removeEventListener('change', this._onSelectChange);
          el.removeEventListener('blur', this._onSelectBlur);
        });
        const buttons = pikadayEl.querySelectorAll('.pika-prev, .pika-next, .pika-button');
        buttons.forEach(el => {
          el.removeEventListener('mousedown', this._onButtonMousedown);
          el.removeEventListener('click', this._onButtonClick);
        });
      }
      this.pikaday?.destroy();
      this.pikaday = null;
    } catch (e) {
      this.log.error('input-date: pikaday destroy failed', e);
    }
  }
  setupPickers() {
    this.createFlatpickr();
  }
  createFlatpickr() {
    const lang = this.intl.primaryLocale?.slice(0, 2) || this.language;
    if (!this.pikaday) {
      const pikaday = new Pikaday({
        field: document.getElementById(`datepicker${this.elementId}`),
        format: this.momentFormat,
        i18n: {
          previousMonth: 'Prev',
          nextMonth: 'Next',
          months: moment.months(),
          weekdays: moment.weekdays(),
          weekdaysShort: moment.weekdaysShort()
        },
        yearRange: this.yearRange,
        firstDay: lang === 'en' ? 0 : 1,
        toString: date => moment(date).format(this.momentFormat),
        parse: str => moment(str, this.momentFormat).toDate(),
        onSelect: date => {
          if (date && !this.isUserInteracting) {
            this._scheduleUpdate(() => this._updateDateTask.perform(date));
          }
        },
        onClose: () => {
          // Pikaday 1.8.2 invokes onClose with NO arguments, so read the date
          // off the instance. onSelect is suppressed while the picker is open
          // (isUserInteracting === true), so this close is the propagation path.
          this.isUserInteracting = false;
          const date = this.pikaday?.getDate();
          if (date) {
            this._scheduleUpdate(() => this._updateDateTask.perform(date));
          }
        },
        onOpen: () => {
          this.isUserInteracting = true;
          this._setupPikadayInteractionListeners();
        },
        onDraw: () => {
          // Re-attach only when open: Pikaday re-renders the DOM on month/year
          // navigation so newly inserted buttons need fresh listener bindings.
          if (this.isUserInteracting) {
            this._setupPikadayInteractionListeners();
          }
        }
      });
      this.pikaday = pikaday;
      this.args.setPika?.(pikaday);
    }
    const format = this.args.pickSeconds ? 'HH:mm:ss' : 'HH:mm';
    this.timeValue = this.originalDate && moment(this.originalDate).isValid() ? moment(this.originalDate).format(format) : null;
    this._updatePickerTask.perform();
  }
  // Metodo helper per controllare l'interazione utente
  _isUserActivelyInteracting() {
    if (!this.pikaday?.isVisible()) {
      return false;
    }
    const pikadayEl = this.pikaday.el;
    if (!pikadayEl) {
      return false;
    }

    // Controlli per vari elementi interattivi
    const interactiveSelectors = ['.pika-select-year', '.pika-select-month', '.pika-prev', '.pika-next', '.pika-button'];

    // Verifica se qualche elemento è attivo/focused
    for (const selector of interactiveSelectors) {
      const element = pikadayEl.querySelector(selector);
      if (element && (element === document.activeElement || element.matches(':focus') || element.matches(':hover'))) {
        return true;
      }
    }

    // Verifica se ci sono select aperti (dropdown)
    const selects = pikadayEl.querySelectorAll('select');
    for (const select of selects) {
      if (select.matches(':focus') || select === document.activeElement) {
        return true;
      }
    }
    return this.isUserInteracting;
  }
  // Setup dei listener per gli elementi interattivi di Pikaday
  _setupPikadayInteractionListeners() {
    if (!this.pikaday?.el) return;
    const pikadayEl = this.pikaday.el;

    // Rimuovi i listener precedenti prima di riaggiungerli
    const selects = pikadayEl.querySelectorAll('select');
    selects.forEach(select => {
      select.removeEventListener('mousedown', this._onSelectMousedown);
      select.removeEventListener('change', this._onSelectChange);
      select.removeEventListener('blur', this._onSelectBlur);
      select.addEventListener('mousedown', this._onSelectMousedown);
      select.addEventListener('change', this._onSelectChange);
      select.addEventListener('blur', this._onSelectBlur);
    });
    const buttons = pikadayEl.querySelectorAll('.pika-prev, .pika-next, .pika-button');
    buttons.forEach(button => {
      button.removeEventListener('mousedown', this._onButtonMousedown);
      button.removeEventListener('click', this._onButtonClick);
      button.addEventListener('mousedown', this._onButtonMousedown);
      button.addEventListener('click', this._onButtonClick);
    });
  }

  // Helper per schedulare aggiornamenti in modo sicuro
  _scheduleUpdate(callback, delay = 10) {
    const id = setTimeout(() => {
      this._timeouts = this._timeouts.filter(t => t !== id);
      if (this.isDestroying || this.isDestroyed) return;
      if (!this.isUserInteracting) {
        callback();
      }
    }, delay);
    this._timeouts.push(id);
  }
  // Funzione unificata per parsing e validazione delle date
  _parseAndValidateDate(value) {
    // Fast-path: the 6 formats that cover >99% of real-world input in the primary locale.
    // Checked first to avoid iterating all 37 formats on every keystroke.
    const fastFormats = [this.momentFormat,
    // DD/MM/YYYY
    'D/M/YYYY', 'DD/MM/YY', 'YYYY-MM-DD', 'DDMMYYYY', 'DDMMYY'];
    for (const format of fastFormats) {
      const date = moment(value, format, true);
      if (date.isValid()) {
        return {
          isValid: true,
          date,
          formatted: date.format(this.momentFormat)
        };
      }
    }

    // Full fallback list for edge-case formats
    const allFormats = ['D/M/YY', 'DD-MM-YYYY', 'D-M-YYYY', 'D-M-YY', 'DD-MM-YY', 'D.M.YYYY', 'DD.MM.YYYY', 'D.M.YY', 'DD.MM.YY', 'YYYY/MM/DD', 'YYYY.MM.DD', 'YY-MM-DD', 'YY/MM/DD', 'YY.MM.DD', 'DMMYYYY', 'YYYYMMDD', 'DMMYY', 'YYMMDD', 'M/D/YYYY', 'MM/DD/YYYY', 'M/D/YY', 'MM/DD/YY', 'MMMM D, YYYY', 'MMM D, YYYY', 'MMMM D, YY', 'MMM D, YY', 'D MMM YYYY', 'D MMMM YYYY', 'D MMM YY', 'D MMMM YY', 'ddd D MMM YYYY', 'ddd D MMM YY'];
    for (const format of allFormats) {
      const date = moment(value, format, true);
      if (date.isValid()) {
        return {
          isValid: true,
          date: date,
          formatted: date.format(this.momentFormat)
        };
      }
    }

    // Controllo per formati numerici compatti
    if (/^\d{4,8}$/.test(value)) {
      const numericDate = this._parseCompactNumericDate(value);
      if (numericDate.isValid()) {
        return {
          isValid: true,
          date: numericDate,
          formatted: numericDate.format(this.momentFormat)
        };
      }
    }

    // Fallback con parsing automatico
    /*if (value) {
      const autoDate = moment(value);
      if (autoDate.isValid()) {
        return {
          isValid: true,
          date: autoDate,
          formatted: autoDate.format(this.momentFormat),
        };
      }
    }*/

    return {
      isValid: false,
      date: moment.invalid()
    };
  }

  // Aggiorna il valore visibile dell'input
  _updateInputValue(formattedValue) {
    const inputElement = document.getElementById(`datepicker${this.elementId}`);
    if (inputElement && inputElement.value !== formattedValue) {
      inputElement.value = formattedValue;
    }
  }
  dateInput(event) {
    const value = event?.target?.value?.trim();

    // Cancella il task precedente e avvia il nuovo
    this._dateInputTask.perform(value);
  }
  dateFocusOut(event) {
    // Cancella il timeout dell'input se presente
    clearTimeout(this._inputTimeout);
    const value = event?.target?.value?.trim() || document.getElementById(`datepicker${this.elementId}`)?.value?.trim();

    // Se il campo è vuoto, cancella sempre indipendentemente da isUserInteracting
    if (!value || value === '') {
      this.args.setDate?.(null);
      this.args.action?.(null);
      this.originalDate = null;
      this._scheduleUpdate(() => this._updatePickerTask.perform(), 50);
      return;
    }

    // Evita conflitti se l'utente sta ancora interagendo con Pikaday
    if (this.isUserInteracting) {
      return;
    }

    // Processa immediatamente al focus out
    this._processDateInput(value);
  }

  // Metodo helper per processare l'input della data
  _processDateInput(value) {
    const result = this._parseAndValidateDate(value);
    if (result.isValid) {
      if (this.pickTime) {
        this._setTimeOnDate(result.date);
      } else {
        const zone = safeTimeZone(this.args.timeZone || 'Europe/Rome');
        result.date = moment.tz({
          year: result.date.year(),
          month: result.date.month(),
          date: result.date.date(),
          hour: 12,
          minute: 0,
          second: 0
        }, zone);
      }
      this.originalDate = result.date.format(this.internalFormat);
      this._scheduleUpdate(() => this._updateDateTask.perform(result.date.format(this.internalFormat)));
    } else {
      // Se il valore non è valido, non aggiornare
      return;
    }
    this._scheduleUpdate(() => this._updatePickerTask.perform(), 50);
  }

  // Helper per parsing di date numeriche compatte
  _parseCompactNumericDate(value) {
    let day, month, year;
    switch (value.length) {
      case 5:
        // DMMYY
        day = value.slice(0, 1);
        month = value.slice(1, 3);
        year = value.slice(3);
        break;
      case 6:
        // DDMMYY
        day = value.slice(0, 2);
        month = value.slice(2, 4);
        year = value.slice(4);
        break;
      case 7:
        // DMMYYYY
        day = value.slice(0, 1);
        month = value.slice(1, 3);
        year = value.slice(3);
        break;
      case 8:
        // DDMMYYYY
        day = value.slice(0, 2);
        month = value.slice(2, 4);
        year = value.slice(4);
        break;
      default:
        return moment.invalid();
    }
    const dayNum = parseInt(day, 10);
    const monthNum = parseInt(month, 10);

    // Validazione base
    if (dayNum < 1 || dayNum > 31 || monthNum < 1 || monthNum > 12) {
      return moment.invalid();
    }
    const format = year.length === 2 ? 'D/M/YY' : 'D/M/YYYY';
    const result = moment(`${day}/${month}/${year}`, format, true);
    return result;
  }
  _setTimeOnDate(date) {
    const timeInput = document.getElementById(`timepicker${this.elementId}`);
    const timeValue = timeInput?.value;
    if (timeValue) {
      let hours,
        minutes,
        seconds = 0;
      if (this.args.pickSeconds) {
        [hours, minutes, seconds] = timeValue.split(':').map(Number);
      } else {
        [hours, minutes] = timeValue.split(':').map(Number);
      }
      if (!isNaN(hours) && !isNaN(minutes)) {
        date.hour(hours).minute(minutes).second(seconds || 0);
      }
    }
  }
}, _BbInputDateComponent._defaultPlaceholder = moment('2015-10-21').format('DD/MM/YYYY'), _BbInputDateComponent), _descriptor = _applyDecoratedDescriptor(_class.prototype, "intl", [inject], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor2 = _applyDecoratedDescriptor(_class.prototype, "log", [inject], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor3 = _applyDecoratedDescriptor(_class.prototype, "pikaday", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return null;
  }
}), _descriptor4 = _applyDecoratedDescriptor(_class.prototype, "timeValue", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return null;
  }
}), _descriptor5 = _applyDecoratedDescriptor(_class.prototype, "originalDate", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return this.args.originalDate;
  }
}), _descriptor6 = _applyDecoratedDescriptor(_class.prototype, "isUserInteracting", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return false;
  }
}), _applyDecoratedDescriptor(_class.prototype, "syncOriginalDate", [action], Object.getOwnPropertyDescriptor(_class.prototype, "syncOriginalDate"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "setupPickers", [action], Object.getOwnPropertyDescriptor(_class.prototype, "setupPickers"), _class.prototype), _descriptor7 = _applyDecoratedDescriptor(_class.prototype, "_updateTimeTask", [_dec], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor8 = _applyDecoratedDescriptor(_class.prototype, "_updateDateTask", [_dec2], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor9 = _applyDecoratedDescriptor(_class.prototype, "_updatePickerTask", [_dec3], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor0 = _applyDecoratedDescriptor(_class.prototype, "_dateInputTask", [_dec4], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _applyDecoratedDescriptor(_class.prototype, "dateInput", [action], Object.getOwnPropertyDescriptor(_class.prototype, "dateInput"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "dateFocusOut", [action], Object.getOwnPropertyDescriptor(_class.prototype, "dateFocusOut"), _class.prototype), _class);
setComponentTemplate(TEMPLATE, BbInputDateComponent);

export { BbInputDateComponent as default };
//# sourceMappingURL=bb-input-date.js.map
